import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import * as api from "../../lib/api";
import { PRIMARY, fmt } from "./shared";

// Shared by the Topic Clusters tab's two views (content clusters, search topics).

export const COVERAGE_COLORS = {
  strong:   { bg: "#ECFDF5", text: "#065F46" },
  moderate: { bg: "#FFFBEB", text: "#B45309" },
  weak:     { bg: "#FFF1F2", text: "#BE123C" },
  poor:     { bg: "#FEF2F2", text: "#DC2626" },
};

export function Chip({ bg, text, children, title }) {
  return (
    <span
      title={title}
      className="px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide rounded-full whitespace-nowrap"
      style={{ background: bg, color: text }}
    >
      {children}
    </span>
  );
}

export function ClusterArticles({ clusterId }) {
  const navigate = useNavigate();
  const [articles, setArticles] = useState(null);

  useEffect(() => {
    api.getCluster(clusterId)
      .then(d => setArticles(
        [...(d.articles || [])].sort((a, b) => (b.gsc_impressions_28d || 0) - (a.gsc_impressions_28d || 0)),
      ))
      .catch(() => setArticles([]));
  }, [clusterId]);

  return (
    <div>
      <p className="text-[10px] tracking-[0.15em] uppercase font-bold text-zinc-400 mb-2">Existing coverage</p>
      {articles === null ? (
        <p className="text-xs text-zinc-400 py-2">Loading articles…</p>
      ) : articles.length === 0 ? (
        <p className="text-xs text-zinc-400 py-2">No articles in this cluster. Run a sync to populate.</p>
      ) : (
        <div className="space-y-1 max-h-80 overflow-y-auto">
          {articles.map(art => (
            <div
              key={art.wp_post_id}
              onClick={() => navigate(`/dashboard/articles/${art.wp_post_id}`)}
              className="flex items-center gap-2 px-3 py-2 bg-white border border-zinc-100 hover:border-zinc-300 rounded-lg cursor-pointer group"
            >
              <span className="text-xs text-zinc-900 truncate flex-1">{art.title}</span>
              <span className="text-[10px] text-zinc-400 tabular-nums whitespace-nowrap">
                {art.gsc_impressions_28d != null
                  ? `${fmt(art.gsc_clicks_28d)} clicks · pos ${fmt(art.gsc_position, 1)}`
                  : "no GSC data"}
              </span>
              {art.wp_url && (
                <a
                  href={art.wp_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={e => e.stopPropagation()}
                  className="opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <ArrowUpRight size={12} className="text-zinc-900" />
                </a>
              )}
            </div>
          ))}
        </div>
      )}
      <button
        onClick={() => navigate(`/dashboard/articles?cluster=${encodeURIComponent(clusterId)}`)}
        className="mt-2 text-[11px] font-bold hover:underline"
        style={{ color: PRIMARY }}
      >
        Open in Articles →
      </button>
    </div>
  );
}

// Search topics and Audience segments are the same groups. Open one topic in the
// Search Performance → Topic Clusters tab: SearchTopics reads (and clears) the focus key.
export function openSearchTopic(navigate, topicId) {
  try {
    localStorage.setItem("sp:topics.view", JSON.stringify("search"));
    localStorage.setItem("sp:topics.focus", JSON.stringify(topicId));
  } catch {}
  navigate("/dashboard/search-performance?tab=topics");
}

// How "priority" is computed (content_gap_map.query_weight), with this row's numbers.
export function priorityHelp(priority, impressions) {
  const w = impressions ? priority / impressions : null;
  return [
    "Priority = sum of each query's impressions × its business weight.",
    "Weight = (commercial value ÷ 5) × buyer fit, × 1.5 if the query lands on a Key Page.",
    "Unscored value or fit counts as 0.5, so unclassified queries weigh 0.25.",
    "Value and fit come from Gap Analysis, grounded in Settings → Business Context.",
    w != null ? `Here: ${Math.round(impressions).toLocaleString()} impressions × avg weight ${w.toFixed(2)} = ${Math.round(priority).toLocaleString()}.` : "",
  ].filter(Boolean).join("\n");
}

// Row metrics read as the formula: impressions × avg business weight = priority.
export function PriorityMetrics({ priority, impressions, clicks }) {
  const w = impressions ? priority / impressions : null;
  // 0.25 = unclassified (0.5 × 0.5); >= 0.5 = mostly valuable, on-target searchers.
  const wColor = w == null ? "#a1a1aa" : w >= 0.5 ? "#065F46" : w < 0.25 ? "#a1a1aa" : "#3f3f46";
  return (
    <div className="flex items-center gap-3 text-right tabular-nums cursor-help" title={priorityHelp(priority, impressions)}>
      <div className="w-14">
        <p className="text-xs font-bold text-zinc-700">{fmt(impressions)}</p>
        <p className="text-[9px] uppercase tracking-wide text-zinc-400">impr</p>
      </div>
      <span className="text-[10px] text-zinc-300">×</span>
      <div className="w-12">
        <p className="text-xs font-bold" style={{ color: wColor }}>{w == null ? "—" : w.toFixed(2)}</p>
        <p className="text-[9px] uppercase tracking-wide text-zinc-400">weight</p>
      </div>
      <span className="text-[10px] text-zinc-300">=</span>
      <div className="w-14">
        <p className="text-xs font-bold text-zinc-900">{fmt(priority)}</p>
        <p className="text-[9px] uppercase tracking-wide text-zinc-400 underline decoration-dotted underline-offset-2">priority</p>
      </div>
      <div className="w-12 ml-1">
        <p className="text-xs font-bold text-zinc-700">{fmt(clicks)}</p>
        <p className="text-[9px] uppercase tracking-wide text-zinc-400">clicks</p>
      </div>
    </div>
  );
}
